import { Container, Particle, ParticleContainer, Rectangle, Sprite, Texture } from 'pixi.js';
import {
  INTERNAL_HEIGHT,
  INTERNAL_WIDTH,
  Owner,
  POOL_CAPACITY,
  ProjectileFlags,
  ProjectileKind,
} from '../../../constants';
import { NodeLensId } from '../../../weapons/definitions';
import type { GameWorld } from '../../../world/GameWorld';
import type { LayerStackSprite } from '../../layerstack/LayerStackSprite';
import type { MobilePoolSnapshot } from '../MobilePoolObserver';
import { NODE_LENS_SLUGS, PROJECTILE_SLUGS } from '../assets/registry';
import type { StackRegistry } from '../assets/StackRegistry';
import { WORLD_TO_R2, placeSprite } from '../scale';
import { LinkLaserVisual } from './LinkLaserVisual';
import type { EntityFamily, FamilyDeps, FrameContext } from './types';

const STATE_NEUTRAL = 'neutral';
const PLAYER_BEAM_COLOR = 0x70fff2;
const ENEMY_BEAM_COLOR = 0xff4f87;
const LASER_SEGMENT_WIDTH = 3;
const SEGMENT_WIDTH = 2;
const SEGMENT_ALPHA = 0.9;
const QUAD_ALPHA = 0.95;
const MIN_QUAD_SIZE = 2;
const MIN_SEGMENT_LENGTH = 1;
const WHITE_WIDTH = Math.max(1, Texture.WHITE.width);
const WHITE_HEIGHT = Math.max(1, Texture.WHITE.height);
const MOBILE_STACK_PREWARM_PER_SLUG = 6;

type ProjectilePool = GameWorld['projectiles'];

/**
 * Authored shots stay layer stacks. Desktop fallback shots use the established
 * Sprite pool. Mobile fallback shots use one PixiJS v8 ParticleContainer with
 * all lightweight Particles allocated before gameplay, eliminating first-use
 * allocation and scene-graph churn during dense bullet patterns.
 */
export class ProjectilesFamily implements EntityFamily {
  private stacks: StackRegistry | null = null;
  private layer: Container | null = null;
  private beamLayer: Container | null = null;
  private mobileBeamContainer: ParticleContainer | null = null;
  private mobilePerformanceMode = false;
  private mobilePool: MobilePoolSnapshot | null = null;
  private readonly sprites: (LayerStackSprite | null)[] = [];
  private readonly slugs: string[] = [];
  private readonly beamSprites: (Sprite | null)[] = [];
  private readonly mobileBeamParticles: Particle[] = [];
  private linkLaser: LinkLaserVisual | null = null;

  init(deps: FamilyDeps): void {
    this.stacks = deps.stacks;
    this.layer = deps.layer;
    this.mobilePerformanceMode = deps.mobilePerformanceMode;
    this.mobilePool = deps.mobilePools?.projectiles ?? null;
    if (this.mobilePerformanceMode) {
      for (let i = 0; i < POOL_CAPACITY.projectiles; i += 1) {
        this.mobileBeamParticles.push(
          new Particle({
            texture: Texture.WHITE,
            anchorX: 0.5,
            anchorY: 0.5,
            alpha: 0,
          }),
        );
      }
      const beamContainer = new ParticleContainer({
        texture: Texture.WHITE,
        particles: this.mobileBeamParticles,
        boundsArea: new Rectangle(
          0,
          0,
          INTERNAL_WIDTH * WORLD_TO_R2,
          INTERNAL_HEIGHT * WORLD_TO_R2,
        ),
        roundPixels: true,
        dynamicProperties: {
          position: true,
          vertex: true,
          rotation: true,
          uvs: false,
          color: true,
        },
      });
      // Added before boss and node families initialize, preserving the shared
      // beam-layer order: projectiles < boss telegraphs < node tethers.
      deps.beamLayer.addChild(beamContainer);
      const linkLaser = new LinkLaserVisual();
      deps.layer.addChild(linkLaser.container);
      this.linkLaser = linkLaser;
      this.mobileBeamContainer = beamContainer;
      this.sprites.length = POOL_CAPACITY.projectiles;
      this.sprites.fill(null);
      this.slugs.length = POOL_CAPACITY.projectiles;
      this.slugs.fill('');
      const prewarmSlugs = new Set(
        [...PROJECTILE_SLUGS, ...NODE_LENS_SLUGS].filter((slug) => slug !== ''),
      );
      for (const slug of prewarmSlugs) {
        deps.stacks.prewarm(slug, MOBILE_STACK_PREWARM_PER_SLUG);
      }
      return;
    }
    this.beamLayer = deps.beamLayer;
    for (let i = 0; i < POOL_CAPACITY.projectiles; i += 1) {
      this.sprites.push(null);
      this.slugs.push('');
      const beam = createBeamSprite();
      beam.visible = false;
      deps.beamLayer.addChild(beam);
      this.beamSprites.push(beam);
    }
    const linkLaser = new LinkLaserVisual();
    deps.layer.addChild(linkLaser.container);
    this.linkLaser = linkLaser;
  }

  sync(world: GameWorld, _frame: FrameContext): void {
    this.linkLaser?.hide();
    if (this.mobilePerformanceMode) {
      this.syncMobile(world);
      return;
    }
    const active = world.projectiles.indices.active;
    for (let i = 0; i < this.sprites.length; i += 1) {
      this.syncSlot(world, i, active[i] !== 0);
    }
  }

  dispose(): void {
    this.mobileBeamParticles.length = 0;
    this.mobileBeamContainer = null;
    this.linkLaser?.dispose();
    this.linkLaser = null;
    this.mobilePool = null;
    this.mobilePerformanceMode = false;
    this.sprites.length = 0;
    this.slugs.length = 0;
    this.beamSprites.length = 0;
    this.layer = null;
    this.beamLayer = null;
    this.stacks = null;
  }

  private syncMobile(world: GameWorld): void {
    const observed = this.mobilePool;
    if (!observed) return;
    for (let i = 0; i < observed.deactivatedCount; i += 1) {
      this.syncSlot(world, observed.deactivatedSlots[i]!, false);
    }
    for (let i = 0; i < observed.activeCount; i += 1) {
      this.syncSlot(world, observed.activeSlots[i]!, true);
    }
  }

  private syncSlot(world: GameWorld, i: number, active: boolean): void {
    const stacks = this.stacks;
    if (!stacks) return;
    const pool = world.projectiles;
    const slug = this.slugs[i]!;
    const playerLinkLaser = active
      && pool.owner[i] === Owner.Player
      && pool.kind[i] === ProjectileKind.Laser
      && (pool.flags[i]! & ProjectileFlags.Segment) !== 0;
    if (playerLinkLaser) {
      this.releaseStack(i, slug);
      if (this.mobilePerformanceMode) this.mobileBeamParticles[i]!.alpha = 0;
      else if (this.beamSprites[i]) this.beamSprites[i]!.visible = false;
      this.linkLaser?.sync(pool, i, world.simTick);
      return;
    }
    const wanted = active ? projectileSlug(pool.kind[i]!, pool.flags[i]!, world.player.lens) : '';
    if (wanted === '') {
      this.releaseStack(i, slug);
      if (this.mobilePerformanceMode) {
        const particle = this.mobileBeamParticles[i]!;
        if (active) drawFallbackParticle(pool, i, particle);
        else particle.alpha = 0;
        return;
      }
      const beam = this.beamSprites[i];
      if (beam && active) drawFallback(pool, i, beam);
      else if (beam) {
        beam.visible = false;
      }
      return;
    }
    if (this.mobilePerformanceMode) this.mobileBeamParticles[i]!.alpha = 0;
    else this.beamSprites[i]!.visible = false;
    let sprite = this.sprites[i];
    if (!sprite || slug !== wanted) {
      this.releaseStack(i, slug);
      sprite = stacks.acquire(wanted, this.layer);
      this.sprites[i] = sprite;
      this.slugs[i] = wanted;
    }
    stacks.applyState(wanted, sprite, STATE_NEUTRAL);
    sprite.setMirror(pool.vx[i]! > 0);
    placeSprite(sprite, pool.x[i]!, pool.y[i]!);
  }

  private releaseStack(slot: number, slug: string): void {
    const sprite = this.sprites[slot];
    if (!sprite || !this.stacks) return;
    this.stacks.release(slug, sprite);
    this.sprites[slot] = null;
    this.slugs[slot] = '';
  }
}

function createBeamSprite(): Sprite {
  const beam = new Sprite(Texture.WHITE);
  beam.anchor.set(0.5);
  beam.roundPixels = true;
  beam.visible = false;
  return beam;
}

/** '' when the projectile has no authored glyph, or is a stretched segment. */
function projectileSlug(kind: number, flags: number, lens: number): string {
  if ((flags & ProjectileFlags.Segment) !== 0) return '';
  if (kind === ProjectileKind.Node) return NODE_LENS_SLUGS[lens] ?? NODE_LENS_SLUGS[NodeLensId.Focus]!;
  return PROJECTILE_SLUGS[kind] ?? '';
}

/**
 * Beams and unmapped kinds. A layer stack cannot stretch, so a segment
 * projectile — every Laser the game spawns — is a sprite rotated and sized
 * between its two endpoints, the same shape PixiRenderer stretched a sprite
 * into.
 */
function drawFallback(pool: ProjectilePool, i: number, sprite: Sprite): void {
  const enemy = pool.owner[i] === Owner.Enemy;
  const color = enemy ? ENEMY_BEAM_COLOR : PLAYER_BEAM_COLOR;
  sprite.visible = true;
  sprite.tint = color;
  if ((pool.flags[i]! & ProjectileFlags.Segment) !== 0) {
    const width = (pool.kind[i] === ProjectileKind.Laser ? LASER_SEGMENT_WIDTH : SEGMENT_WIDTH) * WORLD_TO_R2;
    const x1 = pool.x[i]! * WORLD_TO_R2;
    const y1 = pool.y[i]! * WORLD_TO_R2;
    const x2 = pool.x2[i]! * WORLD_TO_R2;
    const y2 = pool.y2[i]! * WORLD_TO_R2;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const length = Math.max(MIN_SEGMENT_LENGTH, Math.hypot(dx, dy));
    sprite.alpha = SEGMENT_ALPHA;
    sprite.rotation = Math.atan2(dy, dx);
    sprite.setSize(length, width);
    sprite.position.set(Math.round((x1 + x2) / 2), Math.round((y1 + y2) / 2));
    return;
  }
  const size = Math.max(MIN_QUAD_SIZE, Math.round(pool.radius[i]! * 2 * WORLD_TO_R2));
  sprite.alpha = QUAD_ALPHA;
  sprite.rotation = 0;
  sprite.setSize(size, size);
  sprite.position.set(Math.round(pool.x[i]! * WORLD_TO_R2), Math.round(pool.y[i]! * WORLD_TO_R2));
}

function drawFallbackParticle(pool: ProjectilePool, i: number, particle: Particle): void {
  particle.tint = pool.owner[i] === Owner.Enemy ? ENEMY_BEAM_COLOR : PLAYER_BEAM_COLOR;
  if ((pool.flags[i]! & ProjectileFlags.Segment) !== 0) {
    const width = (pool.kind[i] === ProjectileKind.Laser ? LASER_SEGMENT_WIDTH : SEGMENT_WIDTH) * WORLD_TO_R2;
    const x1 = pool.x[i]! * WORLD_TO_R2;
    const y1 = pool.y[i]! * WORLD_TO_R2;
    const x2 = pool.x2[i]! * WORLD_TO_R2;
    const y2 = pool.y2[i]! * WORLD_TO_R2;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const length = Math.max(MIN_SEGMENT_LENGTH, Math.hypot(dx, dy));
    particle.alpha = SEGMENT_ALPHA;
    particle.rotation = Math.atan2(dy, dx);
    particle.scaleX = length / WHITE_WIDTH;
    particle.scaleY = width / WHITE_HEIGHT;
    particle.x = Math.round((x1 + x2) / 2);
    particle.y = Math.round((y1 + y2) / 2);
    return;
  }
  const size = Math.max(MIN_QUAD_SIZE, Math.round(pool.radius[i]! * 2 * WORLD_TO_R2));
  particle.alpha = QUAD_ALPHA;
  particle.rotation = 0;
  particle.scaleX = size / WHITE_WIDTH;
  particle.scaleY = size / WHITE_HEIGHT;
  particle.x = Math.round(pool.x[i]! * WORLD_TO_R2);
  particle.y = Math.round(pool.y[i]! * WORLD_TO_R2);
}
